import { ValueErrorType } from '@sinclair/typebox/errors'

export type ContactFailure = {
  path: string
  message: string
  vars?: Record<string, string | number>
}

export type FailureMessages = Record<string, string>

type FailureSchema = {
  type?: string
  nullable?: boolean
  format?: string
  pattern?: string
  minimum?: number
  maximum?: number
  maxLength?: number
  minLength?: number
  minItems?: number
  maxItems?: number
  messages?: FailureMessages
}

export type SchemaFailure = {
  path?: string
  schema?: FailureSchema
  type?: number
  value?: unknown
  message?: string
  errors?: Iterable<SchemaFailure>[]
}

const CONSTRAINTS: Record<number, string> = {
  [ValueErrorType.ObjectRequiredProperty]: 'required',
  [ValueErrorType.StringMinLength]: 'minLength',
  [ValueErrorType.StringMaxLength]: 'maxLength',
  [ValueErrorType.StringFormat]: 'format',
  [ValueErrorType.StringPattern]: 'pattern',
  [ValueErrorType.NumberMinimum]: 'minimum',
  [ValueErrorType.NumberMaximum]: 'maximum',
  [ValueErrorType.NumberMultipleOf]: 'multipleOf',
  [ValueErrorType.IntegerMinimum]: 'minimum',
  [ValueErrorType.IntegerMaximum]: 'maximum',
  [ValueErrorType.IntegerMultipleOf]: 'multipleOf',
  [ValueErrorType.ArrayMinItems]: 'minItems',
  [ValueErrorType.ArrayMaxItems]: 'maxItems',
  [ValueErrorType.Union]: 'union',
  [ValueErrorType.Kind]: 'union',
  [ValueErrorType.Integer]: 'integer',
}

const FORMAT_MESSAGES: Record<string, string> = {
  email: 'errors.not_an_email',
  uuid: 'errors.must_be_chosen',
  date: 'errors.not_a_date',
}

const blank = (value: unknown) =>
  value === undefined || (typeof value === 'string' && value.trim() === '')

function nulledMember(failure: SchemaFailure): SchemaFailure | undefined {
  if (failure.type !== ValueErrorType.Union || failure.schema?.nullable !== true) return undefined

  for (const member of failure.errors ?? []) {
    for (const inner of member) {
      if (inner.schema?.type !== 'null') return inner
    }
  }

  return undefined
}

function constraintOf(failure: SchemaFailure, schema: FailureSchema): string {
  const named = failure.type === undefined ? undefined : CONSTRAINTS[failure.type]

  if (named !== undefined) return blank(failure.value) ? 'required' : named
  if (schema.format !== undefined) return 'format'
  if (schema.pattern !== undefined) return 'pattern'
  if (typeof schema.minLength === 'number') return 'minLength'
  if (typeof schema.maxLength === 'number') return 'maxLength'
  if (typeof schema.minimum === 'number') return 'minimum'
  if (typeof schema.maximum === 'number') return 'maximum'

  return 'unknown'
}

function defaultMessage(constraint: string, schema: FailureSchema, value: unknown): string {
  switch (constraint) {
    case 'required':
      return 'errors.required'
    case 'minLength':
      return blank(value) || (schema.minLength ?? 0) <= 1 ? 'errors.required' : 'errors.too_short'

    case 'maxLength':
      return 'errors.too_long'
    case 'format':
      return FORMAT_MESSAGES[schema.format ?? ''] ?? 'errors.invalid'
    case 'minimum':
      return 'errors.too_small'
    case 'maximum':
      return 'errors.too_large'
    case 'minItems':
      return 'errors.too_few'
    case 'maxItems':
      return 'errors.too_many'
    case 'union':
      return 'errors.not_a_known_value'
    case 'integer':
      return 'errors.not_a_whole_number'
    default:
      return 'errors.invalid'
  }
}

function varsFor(
  constraint: string,
  schema: FailureSchema,
  message: string,
): ContactFailure['vars'] {
  if (message === 'errors.required') return undefined

  switch (constraint) {
    case 'minLength':
      return schema.minLength === undefined ? undefined : { min: schema.minLength }
    case 'maxLength':
      return schema.maxLength === undefined ? undefined : { max: schema.maxLength }
    case 'minimum':
      return schema.minimum === undefined ? undefined : { min: schema.minimum }
    case 'maximum':
      return schema.maximum === undefined ? undefined : { max: schema.maximum }
    case 'minItems':
      return schema.minItems === undefined ? undefined : { min: schema.minItems }
    case 'maxItems':
      return schema.maxItems === undefined ? undefined : { max: schema.maxItems }
    default:
      return undefined
  }
}

export function schemaFailure(failure: SchemaFailure): ContactFailure {
  const inner = nulledMember(failure)

  if (inner !== undefined) return schemaFailure({ ...inner, path: failure.path })

  const path = String(failure.path ?? '').replace(/^\//, '')
  const schema = failure.schema ?? {}
  const constraint = constraintOf(failure, schema)
  const named = schema.messages?.[constraint]
  const message = named ?? defaultMessage(constraint, schema, failure.value)
  const vars = varsFor(constraint, schema, message)

  return vars === undefined ? { path, message } : { path, message, vars }
}

export const schemaFailures = (all: unknown): ContactFailure[] =>
  (Array.isArray(all) ? (all as SchemaFailure[]) : [])
    .filter((failure) => typeof failure?.path === 'string')
    .map(schemaFailure)

export class InvalidFields extends Error {
  code = 'validation'
  status = 422

  constructor(readonly detail: ContactFailure[]) {
    super('validation')
  }
}
